/**
 * PalettePicker — Shared single-click palette selector.
 *
 * Renders a list of palette rows with round color swatches.
 * Used by both DashboardSettingsPanel and VisualEditor.
 */
import React from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { PALETTE_OPTIONS } from "./visualConfig/palettes";
import type { PaletteOption } from "./visualConfig/types";

// ── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 8px",
    borderRadius: "6px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: "pointer",
    ":hover": {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  swatch: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    marginLeft: "4px",
  },
  badge: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginLeft: "2px",
  },
});

// ── Props ──────────────────────────────────────────────────────────────────

interface PalettePickerProps {
  /** Currently selected palette value */
  value: PaletteOption;
  /** Called when user selects a palette */
  onChange: (palette: PaletteOption) => void;
  /**
   * When set, the palette matching this value shows a "(Default)" badge.
   * Used in chart edit to indicate the dashboard-level theme.
   */
  defaultIndicator?: PaletteOption;
}

// ── Component ──────────────────────────────────────────────────────────────

const PalettePicker: React.FC<PalettePickerProps> = ({
  value,
  onChange,
  defaultIndicator,
}) => {
  const styles = useStyles();

  return (
    <div className={styles.list}>
      {PALETTE_OPTIONS.map((p) => (
        <div
          key={p.value}
          className={styles.row}
          style={
            value === p.value
              ? { borderColor: tokens.colorBrandStroke1, backgroundColor: tokens.colorBrandBackground2 }
              : undefined
          }
          onClick={() => onChange(p.value)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onChange(p.value);
            }
          }}
        >
          {p.colors.map((c, i) => (
            <div key={i} className={styles.swatch} style={{ backgroundColor: c }} />
          ))}
          <span className={styles.label}>
            {p.label}
            {defaultIndicator === p.value && (
              <span className={styles.badge}> (Default)</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

export default PalettePicker;